'use client';

import { useState } from 'react';
import Chips from '@pixum/chip';
import type { CardGroupProps } from '../types';
import { ProductCard } from './ProductCard';
import { GroupedCardComponent } from './GroupedCard';
import clsx from 'clsx';
import { createChipProps } from 'components';
import type { CardNode, GroupedCardNode } from 'data-layer';

function isGroupedCardNode(item: CardNode | GroupedCardNode): item is GroupedCardNode {
  return item.transformerType === 'GroupedCardTransformer';
}

export function CardGroup(props: CardGroupProps) {
  const { cards, device, layout } = props;
  const [currentIndex, setCurrentIndex] = useState(0);
  const cardNodes = cards.filter((card): card is CardNode => !isGroupedCardNode(card));
  const chipProps = createChipProps(cardNodes);

  /**
   * Prevents chip interaction events from bubbling up to the card's click handler.
   * Without this, clicking on a chip would trigger the card's active border state
   * from the CardWithLinkMasking component's event handlers.
   */
  const handleChipsInteraction = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };

  return (
    <>
      {props.cards.map((card, index) => {
        if (isGroupedCardNode(card)) {
          return (
            <li key={`grouped-card-${index}`}>
              <GroupedCardComponent groupedCard={card} device={device} constants={card.constants} />
            </li>
          );
        }

        const activeIndex = cardNodes.findIndex(cn => cn.id === card.id);
        const isActive = currentIndex === activeIndex;

        return (
          <ProductCard
            key={`card-group-${index}`}
            chips={
              <div
                onClick={handleChipsInteraction}
                onMouseDown={handleChipsInteraction}
                onPointerDown={handleChipsInteraction}
                className="card-group__chips-container"
              >
                <Chips
                  activeIndex={currentIndex}
                  chips={chipProps}
                  chipsHeadline=""
                  onSelectedChipChange={setCurrentIndex}
                />
              </div>
            }
            {...card}
            className={clsx(!isActive && 'card--hide')}
            wrapperTag="li"
            layout={layout}
            device={device}
          />
        );
      })}
    </>
  );
}
